import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MoreHorizontal } from "lucide-react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface CTATableProps {
  items: {
    id: number;
    label: string;
    icon: React.ReactNode;
    action: () => void;
    disabled?: boolean;
  }[];
}

export default function CTATable({ items }: CTATableProps) {
  const { t } = useTranslation('common');

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const handleAction = (action: () => void) => {
    if (dropdownRef.current) {
      const parent = dropdownRef.current.closest('[data-state="open"]');
      if (parent) {
        (parent as HTMLElement).click();
      }
    }

    setIsSheetOpen(false);
    action();
  };

  return (
    <div>
      <div className='hidden md:flex'>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <MoreHorizontal
              data-cy='table-actions'
              className='cursor-pointer'
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            ref={dropdownRef}
            className="w-[228px]"
            align="end"
            side="bottom"
            collisionPadding={16}
          >
            {items.map((item, index) => (
              <DropdownMenuItem
                key={index}
                onClick={
                  item?.disabled ? undefined : () => handleAction(item.action)
                }
                data-cy='table-action'
                disabled={item?.disabled}
              >
                {item.icon}
                <DropdownMenuLabel>{t(item.label)}</DropdownMenuLabel>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className='md:hidden'>
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <MoreHorizontal className='cursor-pointer' />
          </SheetTrigger>
          <SheetContent side='bottom'>
            {items.map((item, index) => (
              <div
                key={index}
                className='flex items-center space-x-2 py-4 cursor-pointer'
                onClick={
                  item.disabled ? undefined : () => handleAction(item.action)
                }
                data-cy='table-action'
              >
                {item.icon}
                <DropdownMenuLabel>{t(item.label)}</DropdownMenuLabel>
              </div>
            ))}
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
